import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WARNING_THRESHOLD = 80;
const CRITICAL_THRESHOLD = 95;
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

// Libellés FR + ordre de priorité pour l'affichage
const QUOTA_LABELS = {
  aids_per_month:   { label: 'simulations d’aides',  noun: 'simulation',  period: 'ce mois' },
  documents_total:  { label: 'documents stockés',     noun: 'document',    period: 'au total' },
  ocr_per_month:    { label: 'OCR de documents',          noun: 'OCR',         period: 'ce mois' },
  letters_per_month:{ label: 'lettres générées', noun: 'lettre', period: 'ce mois' },
  chat_per_day:     { label: 'messages copilote',         noun: 'message',     period: 'aujourd’hui' },
};

const QuotaContext = createContext(null);

export const QuotaProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [dismissedAt, setDismissedAt] = useState(() => {
    try { return parseInt(localStorage.getItem('skipya_quota_dismiss_ts') || '0', 10); } catch { return 0; }
  });

  const fetchUsage = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/billing/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsage(data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchUsage();
    const id = setInterval(fetchUsage, 60_000);
    return () => clearInterval(id);
  }, [token, fetchUsage]);

  const dismissUntilTomorrow = useCallback(() => {
    const ts = Date.now();
    setDismissedAt(ts);
    try { localStorage.setItem('skipya_quota_dismiss_ts', String(ts)); } catch {}
  }, []);

  const isDismissed = useMemo(() => Date.now() - dismissedAt < DISMISS_TTL_MS, [dismissedAt]);

  const { warnings, critical, mostSevere } = useMemo(() => {
    const items = usage?.items || {};
    const isPaid = usage && usage.plan_id && usage.plan_id !== 'free';
    if (isPaid) return { warnings: [], critical: [], mostSevere: null };
    const arr = Object.entries(items)
      .filter(([, v]) => v && !v.unlimited && v.limit > 0)
      .map(([key, v]) => ({ key, ...v, meta: QUOTA_LABELS[key] || { label: key, noun: key, period: '' } }))
      .filter((q) => q.percent >= WARNING_THRESHOLD && q.percent < 100);
    const sorted = [...arr].sort((a, b) => b.percent - a.percent);
    return {
      warnings: arr.filter((q) => q.percent < CRITICAL_THRESHOLD),
      critical: arr.filter((q) => q.percent >= CRITICAL_THRESHOLD),
      mostSevere: sorted[0] || null,
    };
  }, [usage]);

  const openUpgrade = useCallback(() => setUpgradeOpen(true), []);
  const closeUpgrade = useCallback(() => setUpgradeOpen(false), []);

  const value = useMemo(() => ({
    usage, loading, refresh: fetchUsage,
    warnings, critical, mostSevere,
    isDismissed, dismissUntilTomorrow,
    upgradeOpen, openUpgrade, closeUpgrade,
    planId: usage?.plan_id || 'free',
  }), [usage, loading, fetchUsage, warnings, critical, mostSevere, isDismissed, dismissUntilTomorrow, upgradeOpen, openUpgrade, closeUpgrade]);

  return <QuotaContext.Provider value={value}>{children}</QuotaContext.Provider>;
};

export const useQuotaUsage = () => {
  const ctx = useContext(QuotaContext);
  if (!ctx) throw new Error('useQuotaUsage must be used inside <QuotaProvider>');
  return ctx;
};
